import { createContext, type ReactNode, useEffect, useMemo, useState } from 'react';

import { useAuth } from '@/auth/useAuth';

export interface AvatarContextValue {
  /** Absolute URL of the current user's uploaded photo, or null to fall back to initials. */
  photoUrl: string | null;
  /** Set (or clear) the current user's photo — called by whichever feature owns the user's profile. */
  setPhotoUrl: (url: string | null) => void;
}

// eslint-disable-next-line react-refresh/only-export-components
export const AvatarContext = createContext<AvatarContextValue | undefined>(undefined);

/**
 * Holds the signed-in user's avatar URL as app-wide client state so every surface — the top-bar
 * avatar, the account profile, and future Recruiter/Company/Messages/Notifications views — renders
 * the same uploaded photo, updating the instant it changes with no page refresh.
 *
 * Why a context and not the GraphQL cache directly: the photo lives on a *feature-specific* entity
 * (the candidate's `TalentProfile`), but the consumers (generic `TopNav`, etc.) must not import a
 * feature's hooks (§3 feature-boundary rule). Each portal's profile loader pushes its photo here via
 * `setPhotoUrl`; generic chrome reads it via `useSessionAvatar`. Cleared automatically on logout.
 */
export function AvatarProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);

  // Clear when the session ends or switches users, so a shared device never shows the last user's
  // photo to the next one (mirrors the Apollo `clearStore()` on logout).
  useEffect(() => {
    if (!user) setPhotoUrl(null);
  }, [user]);

  const value = useMemo<AvatarContextValue>(() => ({ photoUrl, setPhotoUrl }), [photoUrl]);

  return <AvatarContext.Provider value={value}>{children}</AvatarContext.Provider>;
}
